import { ManufacturerService } from './service';
import { Manufacturer, ManufacturersQueryParams } from './types';

/**
 * Manufacturers per page while fetching an export's full result set.
 * Manufacturer pagination is a plain offset/limit over rows (unlike
 * Products' distinct-product-id offset, see `fetchAllFlatProducts.ts`), so
 * a page here is just "how many Manufacturers rows per round-trip". Equal to
 * the server's own page-size clamp (MANUFACTURER_MAX_LIMIT in
 * api/db/manufacturers.js): a larger request is silently clamped, and a page
 * shorter than the requested size is read as "no more rows", which cut the
 * export after the first page whenever the result exceeded the clamp.
 */
const EXPORT_PAGE_SIZE = 1000;

/**
 * Hard ceiling on total manufacturers fetched during an export. If the
 * dataset exceeds this a warning is logged and the loop stops — prevents
 * runaway pagination in unexpected edge cases (mirrors
 * `fetchAllFlatProducts.ts`'s `EXPORT_MAX_PRODUCTS`).
 */
const EXPORT_MAX_MANUFACTURERS = 25000;

/** Per-request timeout for export fetches (ms) — mirrors Products' export. */
export const EXPORT_REQUEST_TIMEOUT_MS = 120_000;

/**
 * Filter params accepted by the full-dataset fetch utility. Same shape as
 * ManufacturersQueryParams but without pagination fields (limit/offset are
 * managed internally).
 */
export type ManufacturersExportFilterParams = Omit<
  ManufacturersQueryParams,
  'limit' | 'offset'
>;

export interface FetchAllManufacturersOptions {
  /** AbortSignal — cancel the in-flight request immediately. */
  signal?: AbortSignal;
}

/**
 * Fetches ALL manufacturer rows matching the current filter by iterating
 * through pages of GET /api/manufacturers (issue #653, Sub-task 11.3) —
 * the "natural follow-on" `ManufacturerExportButton.tsx` flagged when it
 * shipped export of only the currently-loaded page (Task 9, Sub-task 9.3).
 *
 * @param service An instantiated ManufacturerService (caller owns auth/context).
 * @param params  Current filter/sort params (limit/offset omitted).
 * @param options Optional abort signal.
 * @returns       Flat array of every Manufacturer row across all pages.
 */
export async function fetchAllManufacturers(
  service: ManufacturerService,
  params: ManufacturersExportFilterParams,
  options: FetchAllManufacturersOptions = {}
): Promise<Manufacturer[]> {
  const { signal } = options;
  const allRows: Manufacturer[] = [];
  let offset = 0;

  while (true) {
    if (signal?.aborted) {
      throw new DOMException('Export cancelled', 'AbortError');
    }

    const response = await service.getAll(
      { ...params, limit: EXPORT_PAGE_SIZE, offset },
      { signal, timeout: EXPORT_REQUEST_TIMEOUT_MS }
    );

    const { manufacturers, total } = response.data;
    allRows.push(...manufacturers);
    offset += manufacturers.length;

    // Stop when this page returned fewer rows than requested (exhausted),
    // including the zero-rows case (guards against an infinite loop if
    // `manufacturers` is ever empty while `total` is stale/nonzero).
    if (manufacturers.length < EXPORT_PAGE_SIZE || offset >= total) {
      break;
    }

    if (offset >= EXPORT_MAX_MANUFACTURERS) {
      console.warn(
        `[fetchAllManufacturers] Export ceiling reached: ${EXPORT_MAX_MANUFACTURERS} manufacturers fetched. ` +
          `Total reported by API: ${total}. Some records may be omitted.`
      );
      break;
    }
  }

  return allRows;
}
